import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Spinner, MagnifyingGlass, Plus, Trash, PaperPlaneTilt, ArrowsClockwise,
  CaretDown, CaretRight, CaretUp, FolderSimple, CloudArrowDown,
  CheckCircle, Clock, Eye, PencilSimple, GoogleLogo, WarningCircle,
  X, Image as ImageIcon, Bone, ArrowClockwise,
} from '@phosphor-icons/react';
import { useAssetStore } from '../../stores/assetStore';
import { useAuthStore } from '../../stores/authStore';
import { useDriveStore, parseDriveFolderUrl } from '../../stores/driveStore';
import { useSubmissionStore } from '../../stores/submissionStore';
import SmartAssetImporter from '../sidebar/SmartAssetImporter';
import SubmitReviewDialog from '../review/SubmitReviewDialog';
import ConfirmDialog from '../shared/ConfirmDialog';
import DragDropImportOverlay from '../shared/DragDropImportOverlay';
import type { Asset, Submission, SubmissionStatus, DriveSpineSet } from '@spine-workspace/shared';
import type { SavedDriveFolder } from '../../stores/driveStore';

// ── Shared constants ─────────────────────────────────────────

const STATUS_CFG: Record<SubmissionStatus, { label: string; color: string; icon: React.ReactNode }> = {
  waiting_for_review: { label: 'Waiting',     color: 'text-amber-400 bg-amber-400/10 border-amber-400/20',        icon: <Clock size={10} weight="fill" /> },
  in_review:          { label: 'In Review',   color: 'text-blue-400 bg-blue-400/10 border-blue-400/20',          icon: <Eye size={10} weight="fill" /> },
  need_update:        { label: 'Needs Update',color: 'text-red-400 bg-red-400/10 border-red-400/20',             icon: <PencilSimple size={10} weight="fill" /> },
  approved:           { label: 'Approved',    color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: <CheckCircle size={10} weight="fill" /> },
  cancelled:          { label: 'Cancelled',   color: 'text-text-tertiary bg-bg-input border-border',             icon: null },
};

function timeAgo(iso: string) {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

type AssetSort = 'name' | 'type' | 'version' | 'size' | 'status' | 'date';
type SortDir = 'asc' | 'desc';

// ── Main component ───────────────────────────────────────────

export default function AssetsPage() {
  const [tab, setTab] = useState<'assets' | 'drive'>('assets');
  const fetchAssets = useAssetStore(s => s.fetchAssets);

  return (
    <div>
      {/* Sub-tab bar */}
      <div className="flex gap-0 border-b border-border-subtle mb-6 -mt-2">
        {(['assets', 'drive'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2.5 font-sans text-sm font-medium transition-all border-b-2 -mb-px ${
              tab === t ? 'text-accent border-accent' : 'text-text-secondary hover:text-text-primary border-transparent'
            }`}
          >
            {t === 'assets' ? 'My Assets' : 'Google Drive'}
          </button>
        ))}
      </div>

      {tab === 'assets' ? <MyAssetsTab /> : <DriveTab />}

      <DragDropImportOverlay onImportComplete={fetchAssets} />
    </div>
  );
}

// ── My Assets tab ─────────────────────────────────────────────

function MyAssetsTab() {
  const { assets, loading, fetchAssets, deleteAsset } = useAssetStore();
  const { savedFolders, fetchSavedFolders } = useDriveStore();
  const { submissions, submitForReview, fetchSubmissions } = useSubmissionStore();
  const currentUserId = useAuthStore(s => s.user?.id);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Submit dialog state
  const [submitDialogTarget, setSubmitDialogTarget] = useState<{
    folderId: string;
    folderName: string;
    isDriveAsset: boolean;
    assets: Asset[];
    existing: Submission | null;
  } | null>(null);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'spine' | 'image'>('all');
  const [sortKey, setSortKey] = useState<AssetSort>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    fetchAssets();
    fetchSavedFolders();
    fetchSubmissions();
  }, []);

  useEffect(() => {
    if (!showImportMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowImportMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showImportMenu]);

  // Map assetId → submission (Drive folder submissions + direct asset submissions)
  const assetToSubmission = useMemo(() => {
    const map = new Map<string, typeof submissions[0]>();
    // Drive folder submissions
    for (const sf of savedFolders) {
      const sub = submissions.find(s => s.driveFolderId === sf.folderId);
      if (sub) for (const aid of sf.assetIds) map.set(aid, sub);
    }
    // Direct asset submissions (driveFolderId === assetId)
    for (const sub of submissions) {
      if (!map.has(sub.assetId)) {
        const directMatch = assets.find(a => a.id === sub.driveFolderId);
        if (directMatch) map.set(directMatch.id, sub);
      }
    }
    return map;
  }, [savedFolders, submissions, assets]);

  const assetToFolder = useMemo(() => {
    const map = new Map<string, SavedDriveFolder>();
    for (const sf of savedFolders) {
      for (const aid of sf.assetIds) map.set(aid, sf);
    }
    return map;
  }, [savedFolders]);

  const rows = useMemo(() => {
    let list = assets;
    if (typeFilter !== 'all') list = list.filter(a => typeFilter === 'spine' ? a.spineVersion !== 'image' : a.spineVersion === 'image');
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(a => a.name.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      let va: string | number, vb: string | number;
      const subA = assetToSubmission.get(a.id);
      const subB = assetToSubmission.get(b.id);
      if (sortKey === 'name')    { va = a.name.toLowerCase(); vb = b.name.toLowerCase(); }
      else if (sortKey === 'type')    { va = a.spineVersion === 'image' ? 'z' : 'a'; vb = b.spineVersion === 'image' ? 'z' : 'a'; }
      else if (sortKey === 'version') { va = a.spineVersion; vb = b.spineVersion; }
      else if (sortKey === 'size')    { va = a.fileSize; vb = b.fileSize; }
      else if (sortKey === 'status')  { va = subA?.status ?? 'z'; vb = subB?.status ?? 'z'; }
      else                            { va = a.createdAt; vb = b.createdAt; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [assets, typeFilter, search, sortKey, sortDir, assetToSubmission]);

  function toggleSort(key: AssetSort) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function SortIcon({ col }: { col: AssetSort }) {
    if (sortKey !== col) return <CaretDown size={10} className="text-text-tertiary/40" />;
    return sortDir === 'asc' ? <CaretUp size={10} className="text-accent" /> : <CaretDown size={10} className="text-accent" />;
  }

  function Th({ col, label, className = '' }: { col: AssetSort; label: string; className?: string }) {
    return (
      <th onClick={() => toggleSort(col)}
        className={`px-3 py-2.5 text-left text-[11px] font-mono text-text-tertiary uppercase tracking-wide cursor-pointer select-none hover:text-text-primary transition-colors ${className}`}>
        <span className="flex items-center gap-1">{label}<SortIcon col={col} /></span>
      </th>
    );
  }

  const spineCount = assets.filter(a => a.spineVersion !== 'image').length;
  const imageCount = assets.filter(a => a.spineVersion === 'image').length;

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search assets…"
            className="w-full pl-7 pr-3 py-1.5 bg-bg-input border border-border rounded text-xs font-sans text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" />
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-1">
          {([['all','All',assets.length],['spine','Spine',spineCount],['image','Image',imageCount]] as const).map(([v,l,c]) => (
            <button key={v} onClick={() => setTypeFilter(v)}
              className={`px-2.5 py-1 rounded text-[11px] font-mono transition-colors ${
                typeFilter === v ? 'bg-accent/15 text-accent' : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
              }`}>
              {l} {c > 0 && <span className="opacity-70">{c}</span>}
            </button>
          ))}
        </div>

        {/* Import button */}
        <div className="ml-auto relative" ref={menuRef}>
          <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden"
            onChange={async e => {
              const files = Array.from(e.target.files ?? []);
              if (!files.length) return;
              await Promise.all(files.map(f => useAssetStore.getState().uploadImageAsset(f)));
              if (imageInputRef.current) imageInputRef.current.value = '';
            }} />
          <button onClick={() => setShowImportMenu(v => !v)}
            className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-accent/15 text-accent hover:bg-accent/25 text-xs font-sans transition-colors">
            <Plus size={13} />Import
          </button>
          {showImportMenu && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-bg-elevated border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
              <button onClick={() => { setShowImportMenu(false); setShowImportDialog(true); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors">
                <Bone size={13} className="text-accent/70 shrink-0" />Spine Assets
              </button>
              <button onClick={() => { setShowImportMenu(false); imageInputRef.current?.click(); }}
                className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors">
                <ImageIcon size={13} className="text-emerald-400/70 shrink-0" />Image
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {loading && assets.length === 0 ? (
        <div className="flex justify-center py-20"><Spinner size={20} className="animate-spin text-text-tertiary" /></div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-input border-b border-border">
              <tr>
                <Th col="name"    label="Name"     className="w-[32%]" />
                <Th col="type"    label="Type"     className="w-[9%]" />
                <Th col="version" label="Version"  className="w-[9%]" />
                <Th col="size"    label="Size"     className="w-[9%]" />
                <Th col="status"  label="Status"   className="w-[16%]" />
                <Th col="date"    label="Added"    className="w-[11%]" />
                <th className="px-3 py-2.5 w-[14%]" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-14 text-text-tertiary text-xs font-sans">
                  {search || typeFilter !== 'all' ? 'No assets match your filter' : 'No assets yet — import one to get started'}
                </td></tr>
              ) : rows.map(asset => (
                <AssetRow
                  key={asset.id}
                  asset={asset}
                  submission={assetToSubmission.get(asset.id) ?? null}
                  isOwn={asset.ownerId === currentUserId}
                  onDelete={() => deleteAsset(asset.id)}
                  onSubmit={() => {
                    const sf = assetToFolder.get(asset.id);
                    if (sf) {
                      const folderAssets = sf.assetIds.map(id => assets.find(a => a.id === id)).filter(Boolean) as Asset[];
                      setSubmitDialogTarget({
                        folderId: sf.folderId,
                        folderName: sf.folderName,
                        isDriveAsset: true,
                        assets: folderAssets,
                        existing: assetToSubmission.get(asset.id) ?? null,
                      });
                    } else {
                      setSubmitDialogTarget({
                        folderId: asset.id,
                        folderName: asset.name,
                        isDriveAsset: false,
                        assets: [asset],
                        existing: assetToSubmission.get(asset.id) ?? null,
                      });
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import dialog */}
      <Dialog.Root open={showImportDialog} onOpenChange={o => { if (!o) setShowImportDialog(false); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/75 z-40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(480px,calc(100vw-32px))] bg-bg-elevated border border-border rounded-xl shadow-lg focus:outline-none flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-subtle shrink-0">
              <div>
                <Dialog.Title className="font-serif text-md font-medium text-text-primary">Import Spine Assets</Dialog.Title>
                <p className="text-[11px] text-text-tertiary font-sans mt-0.5">Drop files or browse — Spine groups detected automatically</p>
              </div>
              <Dialog.Close className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors shrink-0 ml-3">
                <X size={13} />
              </Dialog.Close>
            </div>
            <div className="overflow-y-auto min-h-0 bg-bg-elevated">
              <SmartAssetImporter onImportComplete={() => setShowImportDialog(false)} />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Submit for Review dialog */}
      {submitDialogTarget && (
        <SubmitReviewDialog
          open={Boolean(submitDialogTarget)}
          onClose={() => setSubmitDialogTarget(null)}
          folderId={submitDialogTarget.folderId}
          folderName={submitDialogTarget.folderName}
          isDriveAsset={submitDialogTarget.isDriveAsset}
          assets={submitDialogTarget.assets}
          existingSubmission={submitDialogTarget.existing}
          onConfirm={async ({ name, note }) => {
            await submitForReview({
              driveFolderId: submitDialogTarget.folderId,
              driveFolderName: submitDialogTarget.folderName,
              assetId: submitDialogTarget.isDriveAsset ? undefined : submitDialogTarget.assets[0]?.id,
              name,
              note,
            });
            await fetchSubmissions();
          }}
        />
      )}
    </div>
  );
}

function AssetRow({ asset, submission, isOwn, onDelete, onSubmit }: {
  asset: Asset;
  submission: Submission | null;
  isOwn: boolean;
  onDelete: () => void;
  onSubmit: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isImage = asset.spineVersion === 'image';
  const navigate = useNavigate();

  function handleSubmit(e: React.MouseEvent) {
    e.stopPropagation();
    onSubmit();
  }

  const cfg = submission ? STATUS_CFG[submission.status] : null;

  return (
    <>
      <tr className="hover:bg-bg-hover transition-colors group">
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            {isImage
              ? <ImageIcon size={13} className="text-emerald-400/70 shrink-0" />
              : <Bone size={13} className="text-accent/70 shrink-0" />}
            <span className="font-sans font-medium text-text-primary truncate" title={asset.name}>{asset.name}</span>
          </div>
        </td>
        <td className="px-3 py-2.5 text-xs font-mono text-text-secondary">{isImage ? 'image' : 'spine'}</td>
        <td className="px-3 py-2.5 text-xs font-mono text-text-tertiary">{isImage ? '—' : (asset.spineVersion || '—')}</td>
        <td className="px-3 py-2.5 text-xs font-mono text-text-tertiary">{fmtSize(asset.fileSize)}</td>
        <td className="px-3 py-2.5">
          {cfg ? (
            <button
              title="Open review scene"
              onClick={() => submission?.reviewSceneId && navigate(`/scene/${submission.reviewSceneId}/edit`)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono font-semibold hover:opacity-80 transition-opacity ${cfg.color}`}>
              {cfg.icon}{cfg.label}
            </button>
          ) : (
            <span className="text-[11px] font-sans text-text-tertiary/50">—</span>
          )}
        </td>
        <td className="px-3 py-2.5 text-xs font-mono text-text-tertiary" title={asset.createdAt}>{timeAgo(asset.createdAt)}</td>
        <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Submit for review */}
            {!isImage && isOwn && (
              <button title={submission ? 'Re-submit for Review' : 'Submit for Review'}
                onClick={handleSubmit}
                className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-sans rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors whitespace-nowrap">
                <PaperPlaneTilt size={10} />
                {submission ? 'Re-submit' : 'Submit'}
              </button>
            )}
            {/* Delete */}
            {isOwn && (
              <button title="Delete asset" disabled={deleting} onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
                className="p-1 rounded text-text-tertiary hover:text-red-400 hover:bg-red-400/10 disabled:opacity-50 transition-colors">
                {deleting ? <Spinner size={11} className="animate-spin" /> : <Trash size={11} />}
              </button>
            )}
          </div>
        </td>
      </tr>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete asset?"
        message={`"${asset.name}" will be permanently deleted. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setConfirmDelete(false);
          setDeleting(true);
          try { await onDelete(); } finally { setDeleting(false); }
        }}
      />
    </>
  );
}

// ── Drive tab ─────────────────────────────────────────────────

function DriveTab() {
  const {
    connected, checking, savedFolders,
    pickedFolder, loadingFolder, folderError,
    importError,
    checkConnection, connectDrive, inspectFolder, clearPicked, importSpineSet,
    fetchSavedFolders,
  } = useDriveStore();
  const { fetchAssets, assets, deleteAsset } = useAssetStore();
  const { submissions, submitForReview, fetchSubmissions } = useSubmissionStore();

  const [urlInput, setUrlInput] = useState('');
  const [inputError, setInputError] = useState('');
  const [importingSetId, setImportingSetId] = useState<string | null>(null);
  const [importedSetIds, setImportedSetIds] = useState<Set<string>>(new Set());
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [submitDialogTarget, setSubmitDialogTarget] = useState<{
    folder: SavedDriveFolder;
    assets: Asset[];
    existing: Submission | null;
  } | null>(null);

  useEffect(() => {
    checkConnection();
    fetchSubmissions();
    const params = new URLSearchParams(window.location.search);
    if (params.get('drive_connected') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      checkConnection();
    }
  }, []);

  function handleCheck() {
    setInputError('');
    const parsed = parseDriveFolderUrl(urlInput);
    if (!parsed) { setInputError('Paste a valid Google Drive folder link or folder ID'); return; }
    inspectFolder(parsed.folderId, parsed.folderName);
    setShowAddFolder(false);
  }

  async function handleImportSet(set: DriveSpineSet) {
    const key = set.json.id; // unique per set even when multiple sets share a folderId
    setImportingSetId(key);
    const jsonStem = set.json.name.replace(/\.json$/i, '').trim();
    const folderName = jsonStem || set.folderName || set.folderId;
    try {
      await importSpineSet({
        folderId: set.folderId,
        folderName,
        jsonFileId: set.json.id,
        atlasFileId: set.atlas.id,
        pngFileIds: set.pngs.map(p => p.id),
      });
      await Promise.all([fetchAssets(), fetchSavedFolders()]);
      setImportedSetIds(prev => new Set(prev).add(key));
      setTimeout(() => setImportedSetIds(prev => { const n = new Set(prev); n.delete(key); return n; }), 4000);
    } finally {
      setImportingSetId(null);
    }
  }

  if (checking || connected === null) {
    return <div className="flex justify-center py-20"><Spinner size={20} className="animate-spin text-text-tertiary" /></div>;
  }

  if (!connected) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="w-14 h-14 rounded-2xl bg-bg-input border border-border flex items-center justify-center">
          <GoogleLogo size={28} weight="light" className="text-text-tertiary" />
        </div>
        <div>
          <p className="font-sans text-sm font-medium text-text-primary mb-1">Connect Google Drive</p>
          <p className="text-xs text-text-secondary leading-relaxed max-w-xs">
            Import Spine assets directly from your Drive folders. One-click sync keeps them up to date.
          </p>
        </div>
        <button onClick={connectDrive}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-sans bg-accent text-white hover:bg-accent/80 transition-colors">
          <GoogleLogo size={14} />Connect Google Drive
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <button onClick={() => setShowAddFolder(v => !v)}
          className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-accent/15 text-accent hover:bg-accent/25 text-xs font-sans transition-colors">
          <Plus size={13} />Add Drive Folder
        </button>
        <button onClick={() => { fetchSavedFolders(); fetchSubmissions(); }}
          className="w-8 h-8 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-colors" title="Refresh">
          <ArrowClockwise size={14} />
        </button>
        <span className="text-xs text-text-tertiary ml-auto">{savedFolders.length} folder{savedFolders.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Add folder inline panel */}
      {showAddFolder && (
        <div className="rounded-lg border border-border bg-bg-surface p-4 space-y-3">
          <p className="text-xs font-sans font-medium text-text-primary">Add a Drive Folder</p>
          <p className="text-[11px] text-text-tertiary font-sans leading-relaxed">
            Open your Spine export folder in Google Drive, copy its sharing link, and paste it below.
          </p>
          <div className="flex gap-2">
            <input value={urlInput} onChange={e => { setUrlInput(e.target.value); setInputError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleCheck()}
              placeholder="https://drive.google.com/drive/folders/…"
              className="flex-1 bg-bg-input border border-border rounded px-3 py-2 text-xs font-mono text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent transition-colors" />
            <button onClick={handleCheck} disabled={!urlInput.trim() || loadingFolder}
              className="px-4 py-2 rounded text-xs font-sans bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-40 transition-colors whitespace-nowrap">
              {loadingFolder ? <Spinner size={11} className="animate-spin" /> : 'Check Folder'}
            </button>
          </div>
          {inputError && (
            <p className="text-[11px] text-red-400 flex items-center gap-1.5">
              <WarningCircle size={11} weight="fill" />{inputError}
            </p>
          )}
        </div>
      )}

      {/* Errors */}
      {folderError && (!pickedFolder || pickedFolder.spineSets.length === 0) && (
        <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-400/10 rounded-lg px-3 py-2.5">
          <WarningCircle size={13} weight="fill" className="shrink-0 mt-px" />{folderError}
        </div>
      )}
      {importError && (
        <div className="flex items-start gap-2 text-xs text-red-400 bg-red-400/10 rounded-lg px-3 py-2.5">
          <WarningCircle size={13} weight="fill" className="shrink-0 mt-px" />{importError}
        </div>
      )}

      {/* Spine set results from inspection */}
      {pickedFolder && (
        <div className="rounded-lg border border-border bg-bg-surface overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-bg-input border-b border-border">
            <span className="text-xs font-mono text-text-secondary uppercase tracking-wide">
              {pickedFolder.spineSets.length > 0
                ? `${pickedFolder.spineSets.length} Spine Set${pickedFolder.spineSets.length > 1 ? 's' : ''} Found`
                : 'No Spine Sets Found'}
            </span>
            <button onClick={clearPicked} className="p-1 rounded text-text-tertiary hover:text-text-primary transition-colors">
              <X size={12} />
            </button>
          </div>
          {pickedFolder.spineSets.length === 0 ? (
            <div className="p-4 space-y-1">
              <p className="text-xs text-text-tertiary">Files seen in this folder:</p>
              {(pickedFolder.debugFiles ?? []).map((f, i) => (
                <div key={i} className="text-[11px] font-mono text-text-tertiary">{f.name} <span className="text-text-tertiary/50">({f.mimeType})</span></div>
              ))}
              {(pickedFolder.debugFiles ?? []).length === 0 && <p className="text-[11px] font-mono text-text-tertiary/50">Folder appears empty</p>}
            </div>
          ) : (
            <div className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {pickedFolder.spineSets.map(set => {
                const key = set.json.id; // unique per set even in flat multi-set folders
                return (
                  <SpineSetCard key={key} set={set}
                    importing={importingSetId === key}
                    imported={importedSetIds.has(key)}
                    onImport={() => handleImportSet(set)} />
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Saved folders table */}
      {savedFolders.length > 0 ? (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg-input border-b border-border">
              <tr>
                <th className="px-4 py-2.5 text-left text-[11px] font-mono text-text-tertiary uppercase tracking-wide w-[32%]">Folder</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-mono text-text-tertiary uppercase tracking-wide w-[9%]">Assets</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-mono text-text-tertiary uppercase tracking-wide w-[16%]">Status</th>
                <th className="px-3 py-2.5 text-left text-[11px] font-mono text-text-tertiary uppercase tracking-wide w-[13%]">Last Synced</th>
                <th className="px-3 py-2.5 w-[30%]" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {savedFolders.map(sf => {
                const folderAssets = sf.assetIds.map(id => assets.find(a => a.id === id)).filter(Boolean) as Asset[];
                const sub = submissions.find(s => s.driveFolderId === sf.folderId && s.status !== 'cancelled') ?? null;
                return (
                  <DriveFolderRow
                    key={`${sf.folderId}:${sf.folderName}`}
                    folder={sf}
                    assets={folderAssets}
                    submission={sub}
                    onSync={async () => {
                      const name = folderAssets[0]?.name ?? sf.folderName;
                      await Promise.all((sf.assetIds.length > 0 ? sf.assetIds : [undefined]).map(assetId =>
                        importSpineSet({ folderId: sf.folderId, folderName: name, assetId })
                      ));
                      await Promise.all([fetchAssets(), fetchSavedFolders()]);
                    }}
                    onDelete={async () => {
                      await Promise.all(folderAssets.map(a => deleteAsset(a.id)));
                      await fetchSavedFolders();
                    }}
                    onSubmit={() => {
                      setSubmitDialogTarget({
                        folder: sf,
                        assets: folderAssets,
                        existing: sub,
                      });
                    }}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      ) : !pickedFolder && (
        <div className="text-center py-14 text-text-tertiary text-xs font-sans">
          No Drive folders added yet — click "Add Drive Folder" to get started
        </div>
      )}

      {/* Submit for Review dialog */}
      {submitDialogTarget && (
        <SubmitReviewDialog
          open={Boolean(submitDialogTarget)}
          onClose={() => setSubmitDialogTarget(null)}
          folderId={submitDialogTarget.folder.folderId}
          folderName={submitDialogTarget.folder.folderName}
          assets={submitDialogTarget.assets}
          existingSubmission={submitDialogTarget.existing}
          onConfirm={async ({ name, note }) => {
            await submitForReview({
              driveFolderId: submitDialogTarget.folder.folderId,
              driveFolderName: submitDialogTarget.folder.folderName,
              assetId: submitDialogTarget.folder.assetIds[0],
              name,
              note,
            });
            await fetchSubmissions();
          }}
        />
      )}
    </div>
  );
}

function DriveFolderRow({ folder, assets, submission, onSync, onDelete, onSubmit }: {
  folder: SavedDriveFolder;
  assets: Asset[];
  submission: Submission | null;
  onSync: () => Promise<void>;
  onDelete: () => Promise<void>;
  onSubmit: () => void;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const cfg = submission ? STATUS_CFG[submission.status] : null;
  const displayName = folder.folderName || `Folder ${folder.folderId.slice(0, 8)}…`;

  async function handleSync(e: React.MouseEvent) {
    e.stopPropagation();
    setSyncing(true);
    try { await onSync(); setSyncDone(true); setTimeout(() => setSyncDone(false), 3000); }
    finally { setSyncing(false); }
  }

  function handleSubmit(e: React.MouseEvent) {
    e.stopPropagation();
    onSubmit();
  }

  return (
    <>
      <tr className="hover:bg-bg-hover transition-colors group cursor-pointer" onClick={() => setExpanded(v => !v)}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {expanded ? <CaretDown size={11} className="text-text-tertiary shrink-0" /> : <CaretRight size={11} className="text-text-tertiary shrink-0" />}
            <FolderSimple size={14} className="text-amber-400 shrink-0" />
            <span className="font-sans font-medium text-text-primary truncate" title={displayName}>{displayName}</span>
          </div>
        </td>
        <td className="px-3 py-3 text-xs font-mono text-text-secondary">{assets.length}</td>
        <td className="px-3 py-3">
          {cfg ? (
            <button onClick={e => { e.stopPropagation(); if (submission?.reviewSceneId) navigate(`/scene/${submission.reviewSceneId}/edit`); }}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono font-semibold hover:opacity-80 transition-opacity ${cfg.color}`}>
              {cfg.icon}{cfg.label}
            </button>
          ) : <span className="text-[11px] text-text-tertiary/50">—</span>}
        </td>
        <td className="px-3 py-3 text-xs font-mono text-text-tertiary">{timeAgo(folder.lastImportedAt)}</td>
        <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1.5 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
            <button title={submission ? 'Re-submit for Review' : 'Submit for Review'}
              onClick={handleSubmit}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-sans rounded bg-accent/10 text-accent hover:bg-accent/20 transition-colors whitespace-nowrap">
              <PaperPlaneTilt size={11} />
              {submission ? 'Re-submit' : 'Submit for Review'}
            </button>
            <button title={syncDone ? 'Synced!' : 'Sync latest from Drive'}
              disabled={syncing}
              onClick={handleSync}
              className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-sans rounded transition-colors disabled:opacity-50 ${
                syncDone ? 'bg-emerald-400/10 text-emerald-400' : 'bg-bg-hover text-text-secondary hover:text-text-primary'
              }`}>
              {syncing ? <Spinner size={9} className="animate-spin" /> : syncDone ? <CheckCircle size={11} weight="fill" /> : <ArrowsClockwise size={11} />}
              {syncDone ? 'Synced' : 'Sync'}
            </button>
            <button title="Remove folder" disabled={deleting} onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-sans rounded bg-bg-hover text-text-tertiary hover:text-red-400 hover:bg-red-400/10 disabled:opacity-50 transition-colors">
              {deleting ? <Spinner size={9} className="animate-spin" /> : <Trash size={11} />}
              Remove
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded asset rows */}
      {expanded && assets.map(asset => (
        <tr key={asset.id} className="bg-bg-deepest/30">
          <td className="pl-12 pr-3 py-2 text-xs font-sans text-text-secondary" colSpan={2}>
            <div className="flex items-center gap-2">
              <Bone size={11} className="text-accent/50 shrink-0" />
              {asset.name}
              <span className="text-[10px] font-mono text-text-tertiary">{asset.spineVersion}</span>
            </div>
          </td>
          <td colSpan={3} className="px-3 py-2 text-[11px] font-mono text-text-tertiary">
            {fmtSize(asset.fileSize)} · added {timeAgo(asset.createdAt)}
          </td>
        </tr>
      ))}
      {expanded && assets.length === 0 && (
        <tr className="bg-bg-deepest/30">
          <td colSpan={5} className="pl-12 pr-3 py-2 text-xs font-sans text-text-tertiary">No assets imported yet</td>
        </tr>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Remove folder?"
        message={`"${displayName}" and all its imported assets will be removed. This cannot be undone.`}
        confirmLabel="Remove"
        danger
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          setConfirmDelete(false);
          setDeleting(true);
          try { await onDelete(); } finally { setDeleting(false); }
        }}
      />
    </>
  );
}

function SpineSetCard({ set, importing, imported, onImport }: {
  set: DriveSpineSet;
  importing: boolean;
  imported: boolean;
  onImport: () => void;
}) {
  const displayName = set.folderName && set.folderName !== set.folderId ? set.folderName : `Folder ${set.folderId.slice(0, 8)}…`;
  return (
    <div className="rounded-lg border border-border bg-bg-input overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle">
        <FolderSimple size={12} className="text-amber-400 shrink-0" />
        <span className="text-xs font-sans text-text-primary truncate flex-1" title={displayName}>{displayName}</span>
        {imported && <CheckCircle size={12} weight="fill" className="text-emerald-400 shrink-0" />}
      </div>
      <div className="p-3 space-y-2">
        <span className="text-[10px] font-mono font-semibold uppercase text-accent tracking-wide">Spine Export Detected</span>
        <div className="space-y-0.5">
          {[set.json, set.atlas, ...set.pngs].map(f => {
            const ext = f.name.endsWith('.json') ? '.json' : f.name.endsWith('.atlas') ? '.atlas' : '.png';
            return (
              <div key={f.id} className="flex items-center gap-2 text-[10px] font-mono text-text-secondary">
                <span className="w-9 shrink-0 text-text-tertiary">{ext}</span>
                <span className="truncate">{f.name}</span>
              </div>
            );
          })}
        </div>
        {imported ? (
          <div className="flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-sans text-emerald-400">
            <CheckCircle size={12} weight="fill" />Imported
          </div>
        ) : (
          <button onClick={onImport} disabled={importing}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded text-[11px] font-sans bg-accent/15 text-accent hover:bg-accent/25 disabled:opacity-50 transition-colors">
            {importing ? <Spinner size={11} className="animate-spin" /> : <CloudArrowDown size={12} weight="fill" />}
            {importing ? 'Importing…' : 'Import as asset'}
          </button>
        )}
      </div>
    </div>
  );
}
